import React from 'react';
import AutoResizeTextarea from '../forms/AutoResizeTextarea';

/**
 * Base question component that provides common functionality
 * All question types should extend this component
 */
const BaseQuestion = ({
  question,
  questionIndex,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  children,
  className = '',
  hideRequired = false,
  questionType,
  questionTypeLabel,
  questionTypeIcon,
}) => {
  const handleTitleChange = (e) => {
    onUpdate(questionIndex, { title: e.target.value });
  };

  const handleDescriptionChange = (e) => {
    onUpdate(questionIndex, { description: e.target.value || null });
  };

  const handleRequiredChange = (e) => {
    onUpdate(questionIndex, { required: e.target.checked });
  };

  return (
    <div className={`glass-panel mb-4 shadow-sm position-relative overflow-hidden ${className}`}>
      {/* Active Indicator Strip (optional, could be conditional) */}
      <div className="position-absolute start-0 top-0 bottom-0 bg-primary" style={{ width: '4px', opacity: 0.8 }}></div>

      <div className="p-3 p-md-4 ps-md-5">
        {/* Top Row: Title, Type, controls */}
        <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-start gap-3 gap-md-4 mb-4">
          <div className="flex-grow-1">
            <AutoResizeTextarea
              placeholder="Question Title"
              value={question.title || ''}
              onChange={handleTitleChange}
              className="form-control form-control-lg border-0 bg-light mb-2 fw-medium question-title-textarea"
              style={{ padding: '0.75rem 1rem' }}
            />
            <AutoResizeTextarea
              placeholder="Description (optional)"
              value={question.description || ''}
              onChange={handleDescriptionChange}
              className="form-control border-0 bg-transparent text-secondary px-3 shadow-none"
              style={{ fontSize: '0.9rem' }}
            />
          </div>

          <div className="d-flex flex-column align-items-end gap-2 question-controls-container">
            {/* Type Indicator */}
            <div className="d-flex align-items-center gap-2 text-secondary bg-light px-3 py-2 rounded-pill">
              <span className="small">{questionTypeIcon}</span>
              <span className="small fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{questionTypeLabel}</span>
            </div>

            {/* Controls */}
            <div className="d-flex align-items-center gap-1 bg-white border rounded-pill p-1 shadow-sm">
              <button
                type="button"
                onClick={() => onMoveUp(questionIndex)}
                disabled={isFirst}
                className="btn btn-sm btn-link text-secondary p-1"
                title="Move up"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 15l-6-6-6 6" /></svg>
              </button>
              <div className="vr h-50 my-auto"></div>
              <button
                type="button"
                onClick={() => onMoveDown(questionIndex)}
                disabled={isLast}
                className="btn btn-sm btn-link text-secondary p-1"
                title="Move down"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
              </button>
              <div className="vr h-50 my-auto"></div>
              <button
                type="button"
                onClick={() => onRemove(questionIndex)}
                className="btn btn-sm btn-link text-danger p-1"
                title="Delete"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="ps-3 border-start border-2 border-light my-4">
          {children}
        </div>

        {/* Footer: Validation/Required Toggle */}
        <div className="d-flex justify-content-end align-items-center border-top pt-3">
          {!hideRequired && (
            <div className="form-check form-switch cursor-pointer">
              <input
                className="form-check-input"
                type="checkbox"
                id={`required-${question.id || questionIndex}`}
                checked={question.required || false}
                onChange={handleRequiredChange}
                style={{ cursor: 'pointer' }}
              />
              <label className="form-check-label text-secondary small fw-medium user-select-none" htmlFor={`required-${question.id || questionIndex}`} style={{ cursor: 'pointer' }}>
                Required
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaseQuestion;
